/**
 * CHI-22: the one eligibility rule for the "Buy Tickets" CTA.
 *
 * Every event CTA surface (EventDetail via Dev3's TicketRegistrationCta,
 * CalendarOfEvents featured / cards / Quick Look / register handler, the Home
 * hero) asks this helper, and renders <BuyTicketsButton> in place of all of its
 * register / sign-in / join / checkout CTAs when it returns true.
 *
 * John, 2026-10-31 1:32 PM CT: every viewer without a webhook-confirmed paid
 * order sees Buy Tickets, signed in or not. A confirmed buyer never does.
 * The buyer signal is Dev2's `hasBlockingTicket` (CHI-20 §3 myTicketsForEvent):
 * an active OR suspended ticket for the event. Dev3's useMyTicketsForEvent carries it.
 */

/** The pricing mode the server stores for Stripe-hosted (internal) checkout. */
export const INTERNAL_STRIPE_PRICING_MODE = "paid_internal" as const;

export const BUY_TICKETS_LABEL = "Buy Tickets";

export type EventCtaEvent = {
  slug: string;
  /** From events.publicPricingPolicy: "free" | "paid_external" | "paid_internal". */
  pricingMode?: string | null;
  isSpecialEvent?: boolean | null;
  hasEnded?: boolean;
  /** Kill switch, read via ticketsCheckoutEnabled(policy). Off (or unknown) hides Buy Tickets everywhere. */
  ticketsCheckoutEnabled?: boolean;
};

export type EventCtaViewer = {
  /**
   * Dev2's `hasBlockingTicket` for this event: the viewer holds an active or
   * suspended ticket (one paid order per buyer per event). Signed out: false.
   */
  hasBlockingTicket?: boolean;
};

export type EventCtaOptions = {
  /**
   * G4.1 PAID_SPECIAL_EVENTS_ENABLED as exposed to the client. It isn't exposed
   * today, so callers leave this unset and special events never get Buy Tickets
   * (they keep G4.1's review flow).
   */
  paidSpecialEventsEnabled?: boolean;
};

/**
 * CHI-20 kill switch (Dev2's TICKETS_CHECKOUT_ENABLED, default false = dark launch).
 * ASSUMED client field name, pending Dev2: `ticketsCheckoutEnabled` on events.publicPricingPolicy.
 * This is the ONE place the client reads it, so a rename is a one-line change here.
 * Fails closed: anything but an explicit `true` is off.
 */
export function ticketsCheckoutEnabled(policy: unknown): boolean {
  return (policy as { ticketsCheckoutEnabled?: unknown } | null | undefined)?.ticketsCheckoutEnabled === true;
}

export function ticketsHref(slug: string): string {
  return `/events/${encodeURIComponent(slug)}/tickets`;
}

/**
 * CHI-20 hero: EventDetail's "My Ticket(s)" content tab. The same key EventDetail's tab list and
 * TicketRegistrationCta's onViewTickets use, so a deep link lands on the panel the card opens.
 */
export const MY_TICKETS_TAB_KEY = "tickets" as const;
export const VIEW_MY_TICKETS_LABEL = "My Tickets";

/** /events/:slug opened on the My Ticket(s) tab (EventDetail reads ?tab= once the tab exists). */
export function myTicketsTabHref(slug: string): string {
  return `/events/${encodeURIComponent(slug)}?tab=${MY_TICKETS_TAB_KEY}`;
}

/**
 * CHI-20 hero: a signed-in buyer with something to view. Mirrors the registration card's
 * "registered" (webhook-issued active tickets with QR codes). Preparing or suspended
 * orders do not yet have viewable tickets.
 */
export function hasTicketsToView(viewer: { activeTicketCount: number; finalizing?: boolean }): boolean {
  return viewer.activeTicketCount > 0;
}

export function shouldShowBuyTickets(event: EventCtaEvent, viewer: EventCtaViewer, options: EventCtaOptions = {}): boolean {
  if (event.ticketsCheckoutEnabled !== true) return false;
  if (event.pricingMode !== INTERNAL_STRIPE_PRICING_MODE) return false;
  if (event.hasEnded) return false;
  if (event.isSpecialEvent === true && options.paidSpecialEventsEnabled !== true) return false;
  return viewer.hasBlockingTicket !== true;
}
